import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import Highlighter from 'react-highlight-words';
import { escapeRegExp } from 'lodash';
import DataGrid, { Cell, Column, HeaderCell } from '@veeva/corgix/data-grid';
import Link from '@veeva/corgix/link';
import useMessageService from '../../services/i18n/useMessageService';
import buildAndSubmitForm from '../../services/buildAndSubmitForm';
import getTargetAttribute from '../../services/getTargetAttribute';

const StudiesGrid = ({ studies, searchTerm }) => {
    const [[studyLabel, noData, noMatches]] = useMessageService([
        'home.studies',
        'home.no_studies',
        'general.no_matches_found',
    ]);

    const displayedStudies = studies.map((study) => {
        return {
            study,
        };
    });

    const renderStudyCell = ({ cellData }) => {
        const { redirectUrl, studyName } = cellData;

        const handleLinkClick = (e) => {
            e.preventDefault();
            buildAndSubmitForm(redirectUrl, {}, { openInNewTab: true });
        };

        const escapedSearchTerm = escapeRegExp(searchTerm);

        return (
            <Cell>
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        height: 100%;
                        word-break: break-all;
                    `}
                >
                    <Link
                        css={css`
                            font-size: 1.1666rem;
                            white-space: pre-line;
                        `}
                        onClick={handleLinkClick}
                        href={redirectUrl}
                    >
                        <Highlighter
                            css={css`
                                width: 100%;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                display: block;
                            `}
                            searchWords={[escapedSearchTerm]}
                            textToHighlight={studyName}
                            highlightTag="strong"
                        />
                    </Link>
                </div>
            </Cell>
        );
    };

    const emptyDataMessage = searchTerm ? noMatches : noData;

    return (
        <DataGrid
            css={css`
                padding: 1rem;
                margin: 0;

                // As of DEV-787031 we are hiding the column header row.
                // If there are enhancements adding extra columns,
                // remove this styling and display the headers properly.
                & > div > div:first-of-type {
                    display: none;
                }
            `}
            data={displayedStudies}
            rowKey="redirectUrl"
            emptyDataMessage={
                <span
                    css={css`
                        font-size: 1.1666rem;
                        font-weight: bold;
                    `}
                    data-target={getTargetAttribute('no-studies-found')}
                >
                    {emptyDataMessage}
                </span>
            }
        >
            <Column
                columnKey="study"
                header={<HeaderCell>{studyLabel}</HeaderCell>}
                cell={renderStudyCell}
                width="100%"
            />
        </DataGrid>
    );
};

StudiesGrid.propTypes = {
    searchTerm: PropTypes.string,
    studies: PropTypes.arrayOf(
        PropTypes.shape({
            redirectUrl: PropTypes.string,
            studyName: PropTypes.string,
        }),
    ),
};

export default StudiesGrid;
